// ExportCsvButton — a small, reusable "Export CSV" action for finance tables.
// Wraps lib/csv.ts exportCsv(): builds a CSV from the rows the page already has
// (respecting current filters) and triggers a download. Disabled (and dimmed)
// when there are no rows so staff get clear feedback. Matches the design-system
// pill-button styling used elsewhere in the finance pages.

import { Download } from "lucide-react";
import { exportCsv, type CsvColumn } from "../../lib/csv";

interface ExportCsvButtonProps<T extends Record<string, unknown>> {
  /** filename (a .csv extension is added if missing) */
  filename: string;
  /** the rows to export — already filtered/sorted by the caller */
  rows: readonly T[];
  /** optional explicit column spec (order + headers + formatters) */
  columns?: readonly CsvColumn<T>[];
  /** button label (defaults to "Export CSV") */
  label?: string;
  className?: string;
}

export default function ExportCsvButton<T extends Record<string, unknown>>({
  filename,
  rows,
  columns,
  label = "Export CSV",
  className = "",
}: ExportCsvButtonProps<T>) {
  const empty = rows.length === 0;
  return (
    <button
      type="button"
      onClick={() => exportCsv(filename, rows, columns)}
      disabled={empty}
      title={
        empty
          ? "No rows to export"
          : `Export ${rows.length.toLocaleString()} row${rows.length === 1 ? "" : "s"} to CSV (current filters)`
      }
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors bg-theme-overlay text-theme-primary hover:bg-theme-overlay/70 disabled:opacity-40 disabled:cursor-not-allowed print:hidden ${className}`}
    >
      <Download size={14} aria-hidden="true" />
      {label}
    </button>
  );
}
